import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import { useEffect } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import type { ProviderDetail } from '../../../../../js/api';
import { ProvidersService } from '../../../../../js/api';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { Checkbox, CheckboxField } from '../../../components/checkbox';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../../components/dialog';
import { FieldValidationErrorMessage } from '../../../components/field-validation-error-message';
import { Description, Field, Fieldset, Label } from '../../../components/fieldset';
import { parseFormDate } from '../../../utils/date';
import { STAFF_PROVIDER_QUERY_KEY, STAFF_PROVIDERS_LIST_QUERY_KEY } from './queryKeys';
import 'react-datepicker/dist/react-datepicker.css';

const schema = z
  .object({
    has_imlc: z.boolean(),
    imlc_expiration_date: z.date().nullable().optional(),
  })
  .refine((values) => values.has_imlc || !values.imlc_expiration_date, {
    message: 'Only a compact-license holder has an IMLC expiry',
    path: ['imlc_expiration_date'],
  });
type FormValues = z.infer<typeof schema>;

/**
 * The Interstate Medical Licensure Compact fields on the clinician: whether they hold
 * a compact license and when it expires. Lives on the Licenses & Certs tab beside the
 * state licenses it stands in for (opportunity matching treats IMLC as "licensed in
 * every participating state").
 */
export function ImlcDialog({
  open,
  onClose,
  provider,
}: {
  open: boolean;
  onClose: () => void;
  provider: ProviderDetail;
}) {
  const queryClient = useQueryClient();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { has_imlc: false, imlc_expiration_date: null },
  });
  const { control, handleSubmit, reset, watch, formState } = form;
  const hasImlc = watch('has_imlc');

  useEffect(() => {
    if (!open) return;
    reset({
      has_imlc: Boolean(provider.has_imlc),
      imlc_expiration_date: provider.imlc_expiration_date
        ? parseISO(provider.imlc_expiration_date)
        : null,
    });
  }, [open, provider, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      ProvidersService.providersPartialUpdate({
        id: provider.id,
        requestBody: {
          has_imlc: values.has_imlc,
          imlc_expiration_date:
            values.has_imlc && values.imlc_expiration_date
              ? parseFormDate(values.imlc_expiration_date)
              : null,
        },
      }),
    onSuccess: () => {
      toast.success('IMLC updated');
      queryClient.invalidateQueries({ queryKey: [STAFF_PROVIDER_QUERY_KEY, provider.id] });
      // the list shows the IMLC column / filter
      queryClient.invalidateQueries({ queryKey: [STAFF_PROVIDERS_LIST_QUERY_KEY] });
      onClose();
    },
    onError: (error: Error) => handleApiError(error, { messagePrefix: 'Error updating IMLC' }),
  });

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle className="tw:!text-2xl tw:!text-brand-green-0 tw:!mt-0 tw:!pt-0">
        Compact License (IMLC)
      </DialogTitle>
      <form onSubmit={handleSubmit((values) => mutation.mutate(values))} data-testid="imlc-form">
        <DialogBody>
          <Fieldset>
            <Field>
              <Controller
                name="has_imlc"
                control={control}
                render={({ field }) => (
                  <CheckboxField>
                    <Checkbox
                      color="brandgreen1"
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={mutation.isPending}
                      data-testid="imlc-has"
                    />
                    <Label>Holds an Interstate Medical Licensure Compact license</Label>
                  </CheckboxField>
                )}
              />
            </Field>
            <Field className="tw:mt-3">
              <Label>IMLC Expiration Date</Label>
              <Controller
                name="imlc_expiration_date"
                control={control}
                render={({ field }) => (
                  <ReactDatePicker
                    className="datepicker-input"
                    wrapperClassName="datepicker-wrapper"
                    selected={field.value ?? null}
                    onChange={(date) => field.onChange(date)}
                    placeholderText={hasImlc ? 'Select expiration date' : 'No compact license'}
                    dateFormat="MM/dd/yyyy"
                    disabled={!hasImlc || mutation.isPending}
                  />
                )}
              />
              {!hasImlc && <Description>Clearing the compact flag clears the expiry.</Description>}
              {formState.errors.imlc_expiration_date && (
                <FieldValidationErrorMessage
                  message={formState.errors.imlc_expiration_date.message}
                />
              )}
            </Field>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button
            type="button"
            color="zinc-outline"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" color="brandgreen1" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
